import React, { useEffect, useState } from "react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { toast } from "sonner";
import { AlertTriangle, Package, Sparkles, RefreshCw, TrendingDown } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const RISK_TONES = {
    out_of_stock: "bg-rose-100 text-rose-800 border-rose-200",
    critical: "bg-amber-100 text-amber-800 border-amber-200",
    medium: "bg-blue-100 text-blue-800 border-blue-200",
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const RISK_LABELS = {
    out_of_stock: "Out of stock",
    critical: "Critical (<10 days)",
    medium: "Medium (10-30 days)",
    low: "Low",
};

const AdminInventoryForecast = () => {
    const [risk, setRisk] = useState("critical");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suggesting, setSuggesting] = useState({}); // {product_id: bool}
    const [suggestions, setSuggestions] = useState({}); // {product_id: {qty, rationale}}

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (risk !== "all") params.set("risk", risk);
        api.get(`/admin/inventory/forecast?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };
    useEffect(load, [risk]); // eslint-disable-line

    const getSuggestion = async (pid) => {
        setSuggesting((s) => ({ ...s, [pid]: true }));
        try {
            const { data: out } = await api.post(`/admin/inventory/forecast/${pid}/suggest-reorder`);
            setSuggestions((s) => ({ ...s, [pid]: out }));
            toast.success(`Suggested: ${out.suggested_reorder_qty} units`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Suggestion failed");
        } finally {
            setSuggesting((s) => ({ ...s, [pid]: false }));
        }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout
                title="Inventory forecast"
                subtitle={data ? `${data.total} product(s) in this view · generated ${new Date(data.generated_at).toLocaleTimeString()}` : "Loading…"}
                action={
                    <button onClick={load} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs hover:bg-zinc-50" data-testid="forecast-refresh-btn">
                        <RefreshCw className="h-3 w-3" /> Refresh
                    </button>
                }
            >
                {/* Risk tabs */}
                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="forecast-risk-tabs">
                    {["out_of_stock", "critical", "medium", "low", "all"].map((r) => (
                        <button
                            key={r}
                            onClick={() => setRisk(r)}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${risk === r ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`}
                            data-testid={`forecast-risk-${r}`}
                        >
                            {r === "all" ? "All" : RISK_LABELS[r]}
                            {data?.counts_by_risk?.[r] != null && r !== "all" && (
                                <span className="ml-1.5 text-[10px] opacity-70">({data.counts_by_risk[r]})</span>
                            )}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-sm text-zinc-500">Loading…</p>
                ) : !data || data.items.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">No products in this view.</p>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white">
                        <table className="w-full text-sm" data-testid="forecast-table">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Product</th>
                                    <th className="px-3 py-3 text-right">Stock</th>
                                    <th className="px-3 py-3 text-right">30d sold</th>
                                    <th className="px-3 py-3 text-right">Days left</th>
                                    <th className="px-3 py-3">Risk</th>
                                    <th className="px-3 py-3 text-right">Suggested reorder</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {data.items.map((r) => {
                                    const sug = suggestions[r.product_id];
                                    return (
                                        <tr key={r.product_id} data-testid={`forecast-row-${r.product_id}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {r.image ? (
                                                        <img src={r.image} alt="" className="h-8 w-8 rounded-md object-cover" />
                                                    ) : (
                                                        <span className="grid h-8 w-8 place-items-center rounded-md bg-zinc-100"><Package className="h-3.5 w-3.5 text-zinc-400" /></span>
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-zinc-900 line-clamp-1">{r.name}</p>
                                                        <p className="text-[10px] text-zinc-500">{r.brand}{r.pack_size ? ` · ${r.pack_size}` : ""}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-right font-mono text-sm">{r.current_stock}</td>
                                            <td className="px-3 py-3 text-right font-mono text-xs text-zinc-700">{r.units_30d}</td>
                                            <td className="px-3 py-3 text-right">
                                                {r.days_until_stockout == null ? (
                                                    <span className="text-xs text-zinc-400">—</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium">
                                                        {r.days_until_stockout < 10 && <TrendingDown className="h-3 w-3 text-rose-500" />}
                                                        {r.days_until_stockout}d
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3">
                                                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${RISK_TONES[r.risk]}`}>
                                                    {r.risk === "out_of_stock" && <AlertTriangle className="h-3 w-3" />}
                                                    {r.risk.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                {sug ? (
                                                    <div className="inline-block text-right" data-testid={`forecast-suggestion-${r.product_id}`}>
                                                        <p className="text-sm font-semibold text-brand-700">{sug.suggested_reorder_qty} units</p>
                                                        <p className="max-w-[180px] truncate text-[10px] text-zinc-500" title={sug.rationale}>{sug.rationale}</p>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => getSuggestion(r.product_id)}
                                                        disabled={suggesting[r.product_id]}
                                                        className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
                                                        data-testid={`forecast-suggest-btn-${r.product_id}`}
                                                    >
                                                        <Sparkles className="h-3 w-3" /> {suggesting[r.product_id] ? "…" : "Get AI suggestion"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="mt-4 text-[10px] text-zinc-400">
                    Velocity = units sold in the last 30 days ÷ 30 (falls back to 90d window if 30d is sparse). Days-left = current_stock ÷ velocity.
                </p>
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminInventoryForecast;
